import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { API_BASE_URL } from "@/api/helpers";
import { log } from "@/core/logger";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Member } from "@/types/Member";

interface UploadAvatarParams {
  memberId: string;
  avatarFile: File;
}

interface UploadAvatarResponse {
  success: boolean;
  member: Member;
  avatarUrl: string;
}

/**
 * Hook to upload a new avatar for a member
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UploadAvatarParams): Promise<UploadAvatarResponse> => {
      try {
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append("avatar", params.avatarFile);

        const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${params.memberId}/avatar`, {
          method: "PATCH",
          credentials: "include",
          body: formData,
          // Don't set Content-Type header - browser will set it with boundary for FormData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || "Failed to upload avatar");
        }

        const data = await response.json();
        return data;
      } catch (error) {
        log("useUploadAvatar", "Failed to upload avatar", error);
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["member", variables.memberId] });
      queryClient.invalidateQueries({ queryKey: ["members"] });

      log("useUploadAvatar", `Successfully uploaded avatar for member: ${variables.memberId}`);
    },
    onError: (error: Error) => {
      log("useUploadAvatar", "Error uploading avatar", error);
    },
  });
}
